import { getAuth } from "firebase/auth";

export type TResponse<T> = {
  content: T[];
  total: number;
  offset: number;
  limit: number;
};

export const TResponse = {
  content: [],
  total: 0,
  offset: 0,
  limit: 0,
};

export type TConfig = {
  baseUrl: string;
  socketUrl: string;
  auth: {
    google: {
      clientId: string;
      redirectUri: string;
      scopes: string[];
      photos: {
        redirectUri: string;
        scopes: string[];
      };
    };
    spotify: {
      clientId: string;
      redirectUri: string;
      scopes: string;
    };
    twitter: {
      clientId: string;
      redirectUri: string;
      scopes: string;
    };
  };
};

export type ApiWorkerProps = {
  url: string;
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: any;
  extraHeaders?: { [key: string]: string | number };
  timeout?: number;
  withCredentials?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
};

let settings: TConfig | null = null;
let fetchInstance: Promise<void> | null = null;

export async function getConfig(): Promise<TConfig> {
  if (settings) {
    // Return cached settings if already loaded
    return settings;
  }

  if (!fetchInstance) {
    // Start a fetch operation if none is in progress
    fetchInstance = (async () => {
      try {
        const response = await fetch("/config.json");

        if (!response.ok) {
          throw new Error(`Failed to fetch config: ${response.statusText}`);
        }

        const config = await response.json();
        settings = config as TConfig; // Explicitly cast to TConfig
      } finally {
        fetchInstance = null; // Reset the fetch instance on completion
      }
    })();
  }

  await fetchInstance; // Wait for the fetch to complete

  if (!settings) {
    // Handle unexpected cases where settings remain null
    throw new Error("Config fetch failed unexpectedly.");
  }

  return settings;
}

export async function apiWorker({
  url,
  method = "GET",
  body = null,
  extraHeaders = {},
  timeout = 60000,
  withCredentials = true,
  onSuccess,
  onError,
}: ApiWorkerProps) {
  try {
    let headers: any = {
      "Content-Type": "application/json",
    };

    const config = await getConfig();

    if (withCredentials) {
      const userToken = await getAuth().currentUser?.getIdTokenResult(true);

      if (userToken?.token) {
        headers = {
          ...headers,
          Authorization: `Bearer ${userToken?.token}`,
        };
      }
    }

    if (extraHeaders) {
      headers = {
        ...headers,
        ...extraHeaders,
      };
    }

    let newUrl = config.baseUrl + url;
    let content = null;

    if (method != "GET" && body) {
      content = JSON.stringify(body);
    }

    const abortController = new AbortController();

    const _timeout = setTimeout(() => abortController.abort(), timeout);

    const response = await fetch(url.startsWith("http") ? url : newUrl, {
      method,
      headers,
      body: content,
      signal: abortController.signal,
    });

    if (!response.ok) {
      return onError?.(await response.json());
    }

    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      const data = await response.json();
      clearTimeout(_timeout);
      if (data.error || data.code != null || data.status >= 400) {
        return onError?.(data);
      }
      return onSuccess?.(data);
    } else {
      const data = await response.text();
      clearTimeout(_timeout);
      return onSuccess?.(data);
    }
  } catch (exc) {
    console.error(exc);
    onError?.(exc);
  }
}
